import { uniqBy } from 'lodash-es'
import { createSelector } from 'reselect'

export const showFiltersBarSelector = (state) => (
  state.scheduled_events.showFiltersBar
)

export const showBuffersSelector = (state) => (
  state.scheduled_events.showBuffers
)

export const loadingSelector = (state) => (
  state.scheduled_events.loading
)

export const pendingEventsSelector = (state) => (
  state.scheduled_events.pending_events
)

export const metaSelector = (state) => (
  state.scheduled_events.meta
)

export const paginationSelector = (state) => (
  state.scheduled_events.pagination
)

export const dayListSelector = (state) => (
  state.scheduled_events.collection
)

export const pollResultsSelector = (state) => (
  state.scheduled_events.pollResults
)

export const pollBookingSelector = (state) => (
  state.scheduled_events.pollBooking
)

export const bookMeetingWithContactSelector = (state) => (
  state.scheduled_events.bookMeetingWithContact
)

export const editBufferPopupSelector = (state) => (
  state.scheduled_events.editBufferPopup
)

const pollSpotsSelector = createSelector(
  pollResultsSelector,
  ({ data }) => data.spots
)

export const pollResultsInviteesSelector = createSelector(
  pollSpotsSelector,
  (spots) => (
    uniqBy(spots.map(
      (item) => item.invitees
    ).flat(), 'id')
  )
)

export const eventListSelector = (state) => (
  state.scheduled_events.collection.map(
    (group) => group.events
  ).flat()
)

const popupsSelector = (state) => (
  state.scheduled_events.popups
)

export const exportErrorSelector = (state) => (
  state.scheduled_events.export.error
)

export const filtersErrorSelector = (state) => (
  state.scheduled_events.filters_error
)

const inviteeListSelector = (state) => (
  eventListSelector(state).map(
    (event) => event.invitees.collection
  ).flat()
)

export const activeEventIdSelector = (state) => (
  state.scheduled_events.activeEventId
)

export const makeEventListSelector = (date) => (state) => {
  const day = state.scheduled_events.collection.find(
    (group) => (group.date === date)
  )
  return day ? day.events : []
}

export const makeEventSelector = (eventId) => createSelector(
  eventListSelector,
  (events) => (events.find((event) => event.id === eventId))
)

export const hasInternalConflict = (event) => createSelector(
  eventListSelector,
  (events) => {
    const onSamePageEvents = events.filter((e) => event.internal_conflict_event_ids?.includes(e.id))

    if (onSamePageEvents.length > 0) {
      return onSamePageEvents.some((e) => !e.cancelled)
    }

    return event.internal_conflict_event_ids?.length > 0
  }
)

export const makePendingEventSelector = (eventId) => (state) => {
  const pendingEvent = state.scheduled_events.pending_events.find(
    (event) => (event.pending_id === eventId)
  )

  return pendingEvent
}

export const makeInviteeSelector = (inviteeId) => createSelector(
  inviteeListSelector,
  (invitees) => (
    invitees.find(
      (invitee) => invitee.id === inviteeId
    )
  )
)

export const isEditingSelector = createSelector(
  inviteeListSelector,
  eventListSelector,
  (invitees, events) => (
    invitees.some(
      (invitee) => invitee.editing === true
    ) || events.some((event) => !!event.editing)
  )
)

export const isExportConfirmationVisibleSelector = createSelector(
  popupsSelector,
  (popups) => popups.export_confirmation
)

const eventMembershipsSelector = (eventId) => createSelector(
  eventListSelector,
  (events) => {
    const event = events.find((e) => e.id === eventId)
    return event?.event_memberships
  }
)

const groupedMembershipsByBuffersSelector = (eventId) => createSelector(
  eventMembershipsSelector(eventId),
  (memberships) => {
    const grouped = memberships.reduce((acc, membership) => {
      const {
        has_before_buffer: hasBeforeBuffer,
        has_after_buffer: hasAfterBuffer,
        buffered_start_time: startKey,
        buffered_end_time: endKey
      } = membership

      if (hasBeforeBuffer) {
        acc.startTimes[startKey] = acc.startTimes[startKey] || []
        acc.startTimes[startKey].push(membership)
      }

      if (hasAfterBuffer) {
        acc.endTimes[endKey] = acc.endTimes[endKey] || []
        acc.endTimes[endKey].push(membership)
      }

      return acc
    }, { startTimes: {}, endTimes: {} })
    return grouped
  }
)

export const sortedGroupedMembershipsByBuffersSelector = (eventId) => createSelector(
  groupedMembershipsByBuffersSelector(eventId),
  (groupedMemberships) => {
    const sortedStartTimes = {}
    const sortedEndTimes = {}

    Object.keys(groupedMemberships.startTimes)
      .sort((a, b) => new Date(a) - new Date(b))
      .forEach((key) => {
        sortedStartTimes[key] = groupedMemberships.startTimes[key]
      })

    Object.keys(groupedMemberships.endTimes)
      .sort((a, b) => new Date(a) - new Date(b))
      .forEach((key) => {
        sortedEndTimes[key] = groupedMemberships.endTimes[key]
      })

    return { startTimes: sortedStartTimes, endTimes: sortedEndTimes }
  }
)
